import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { RefreshCw } from 'lucide-react';
import { Card, CardHead, INK } from '../abf/onboarding/review/reviewStyles';
import { Note, SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import { Table, Toolbar, when, words } from '../abf/board/boardShared';
import { getPcActivity, pcErrorMessage } from './api';
import type { PcActivity } from './types';

/**
 * The Activity workspace of a private credit facility (PC workflow parity, slice 4): one timeline
 * across servicing, underwriting, covenants, exceptions, amendments, tickets, documents and the
 * queue, each line naming the record it comes from. Read-only.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.5;
  h2, p { margin: 0; } h2 { font-size: 16px; color: ${INK.strong}; }
  .wf { font-size: 10.5px; text-transform: uppercase; letter-spacing: .07em; color: ${INK.faint}; font-weight: 800; }
  code { font-size: 11px; color: ${INK.faint}; }
`;
const PAGE = 50;

const ActivityWorkspace: React.FC<{ loanId: number; refreshKey?: number | string }> = ({ loanId, refreshKey }) => {
  const [data, setData] = useState<PcActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState('');
  const [actor, setActor] = useState('');
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await getPcActivity(loanId, { workflow: workflow || undefined, actor: actor || undefined, limit: PAGE, offset })); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The activity could not be read.')); }
    finally { setBusy(false); }
  }, [loanId, workflow, actor, offset]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  if (!data) return <Note role={error ? 'alert' : 'status'}><p>{error || "Reading the facility's activity…"}</p></Note>;

  return <Shell aria-label="Activity">
    <Card>
      <CardHead>
        <div><h2>Activity</h2><p>{data.total} event{data.total === 1 ? '' : 's'} across the facility's workflows, newest first. Each names the record it comes from.</p></div>
        <SmallButton type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={14} /> Refresh</SmallButton>
      </CardHead>
      <Toolbar>
        <label>Workflow <select value={workflow} onChange={e => { setOffset(0); setWorkflow(e.target.value); }}><option value="">all</option>{data.workflows.map(w => <option key={w} value={w}>{words(w)}</option>)}</select></label>
        <label>Actor <select value={actor} onChange={e => { setOffset(0); setActor(e.target.value); }}><option value="">anyone</option>{data.actors.map(a => <option key={a} value={a}>{a}</option>)}</select></label>
        <span style={{ color: INK.faint, fontSize: 12 }}>{data.offset + 1}–{Math.min(data.offset + data.limit, data.total)} of {data.total}</span>
        <SmallButton type="button" disabled={data.offset === 0} onClick={() => setOffset(Math.max(0, data.offset - PAGE))}>Newer</SmallButton>
        <SmallButton type="button" disabled={data.offset + data.limit >= data.total} onClick={() => setOffset(data.offset + PAGE)}>Older</SmallButton>
      </Toolbar>
      {error && <Note role="alert"><p>{error}</p></Note>}
      <Table style={{ margin: 0 }}>
        <thead><tr><th>When</th><th>Workflow</th><th>What</th><th>Who</th><th>Record</th></tr></thead>
        <tbody>
          {data.events.map((e, i) => <tr key={`${e.recordTable}-${e.recordId}-${e.kind}-${i}`}>
            <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(e.at)}</td>
            <td><span className="wf">{words(e.workflow)}</span></td>
            <td><strong>{e.label}</strong>{e.detail && <><br /><small className="muted">{e.detail}</small></>}</td>
            <td>{e.actor || <span className="muted">system</span>}</td>
            <td><code>{e.recordTable}{e.recordId ? ` #${e.recordId.length > 12 ? e.recordId.slice(0, 8) : e.recordId}` : ''}</code></td>
          </tr>)}
          {data.events.length === 0 && <tr><td colSpan={5} className="muted">Nothing recorded under these filters.</td></tr>}
        </tbody>
      </Table>
    </Card>
  </Shell>;
};

export default ActivityWorkspace;
